/**
 * Per-tier features shown on plan cards in the pricing table.
 * Features are only displayed if they apply to the plan (no cross/X for unavailable ones).
 * Credits and concurrent threads are shown separately from plan data.
 */

export interface PlanTierFeature {
  label: string;
  // Shown on the opposite side of the upgrade/downgrade feature-delta row when the
  // feature is a pure gain (upgrade) or loss (downgrade) — replaces the em dash
  // with a descriptive "without it" phrase. Omit for features that don't need one.
  withoutLabel?: string;
  infoTooltip?: string;
  badge?: "new";
}

// Support-level labels surfaced in the Upgrade/Downgrade modals and UpgradeCard.
// Centralised so the three surfaces can't drift apart if copy changes.
export const SUPPORT_LABEL = {
  FREE: "Community support",
  PAID: "Priority support",
} as const;

const AI_PARSER_PLG: PlanTierFeature = {
  label: "Up to 5 AI Parsers",
  badge: "new",
  infoTooltip: "Each parser create or edit costs 30,000 credits. Each create or edit counts toward your 5-parser limit.",
};

/**
 * @deprecated  for the ChangeSubscriptionModal we compute it on-the-fly based on plan attributes. should do that in all the other cases as well.
 */
export const PLAN_TIER_FEATURES: Record<string, PlanTierFeature[]> = {
  free: [
    { label: "Limited crawler access", infoTooltip: "No access to link depth and scheduling" },
    {
      label: "1 AI Parser",
      badge: "new",
      infoTooltip: "Create 1 AI Parser. Upgrade to a paid plan to create up to 5 and edit them.",
    },
  ],
  hobby: [
    { label: "Ultra Premium", withoutLabel: "No premium" },
    AI_PARSER_PLG,
    { label: "Full crawler access" },
  ],
  startup: [
    { label: "Ultra Premium", withoutLabel: "No premium" },
    AI_PARSER_PLG,
    { label: "Full crawler access" },
  ],
  business: [
    { label: "Ultra Premium", withoutLabel: "No premium" },
    AI_PARSER_PLG,
    { label: "Full crawler access" },
    { label: "Unlimited analytics history", withoutLabel: "Limited analytics history" },
  ],
  scaling: [
    { label: "Ultra Premium", withoutLabel: "No premium" },
    AI_PARSER_PLG,
    { label: "Full crawler access" },
    { label: "Unlimited analytics history", withoutLabel: "Limited analytics history" },
    { label: "Pay as you go", withoutLabel: "No PAYG", infoTooltip: "Avoid interruptions, enable pay-as-you-go and set a spending cap." },
  ],
  professional: [
    { label: SUPPORT_LABEL.PAID, withoutLabel: SUPPORT_LABEL.FREE },
    { label: "Ultra Premium", withoutLabel: "No premium" },
    AI_PARSER_PLG,
    { label: "Full crawler access" },
    { label: "Unlimited analytics history", withoutLabel: "Limited analytics history" },
    { label: "Pay as you go", withoutLabel: "No PAYG", infoTooltip: "Avoid interruptions, enable pay-as-you-go and set a spending cap." },
  ],
  advanced: [
    { label: SUPPORT_LABEL.PAID, withoutLabel: SUPPORT_LABEL.FREE },
    { label: "Ultra Premium", withoutLabel: "No premium" },
    AI_PARSER_PLG,
    { label: "Full crawler access" },
    { label: "Unlimited analytics history", withoutLabel: "Limited analytics history" },
    { label: "Pay as you go", withoutLabel: "No PAYG", infoTooltip: "Avoid interruptions, enable pay-as-you-go and set a spending cap." },
  ],
};
